// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import type {
  DistributionListGridModel,
  DistributionListFilterOptionsModel
} from '../../../state_manager/data_models/distribution_list_grid_model.ts'
import type { DistributionListRecipient } from '../../../state_manager/data_models/distribution_list_recipients_model.ts'
import type { DistributionListResolvePayload } from '../../../state_manager/interfaces/distribution_lists.ts'
import {
  fetchDistributionListsFiltered,
  fetchDistributionListFilterOptions,
  fetchDistributionListDetail,
  resolveDistributionList
} from '../services/distributionListsService.ts'

/**
 * useSendCommunication — the Phase 5 Usage ("Send Communication") screen's own
 * data layer (decision §2.15). Screen-local, NO Redux (the Phase 2/3/4
 * precedent §2.12e/§2.15a). It REUSES the Phase 4 read endpoints verbatim:
 *  - the saved-list picker            ← view__distribution_lists (grid)
 *  - the editable-filter pre-populate ← view__distribution_list_detail (§2.13b
 *    seeds accounts_add/contacts_add from the persisted force-include overlay)
 *  - every select's option universe   ← view__distribution_list_filter_options
 *  - Generate (resolve recipients)    ← view__distribution_list (no new RPC;
 *    the controller wraps the bare array — §2.15c)
 * It issues NO writes (§2.6 — usage-time amendments are session-only).
 */
export const useSendCommunication = () => {
  const [savedLists, setSavedLists] = useState<DistributionListGridModel[]>([])
  const [listsLoading, setListsLoading] = useState(false)
  const [options, setOptions] = useState<DistributionListFilterOptionsModel | null>(null)
  const [optionsLoading, setOptionsLoading] = useState(false)
  const [recipients, setRecipients] = useState<DistributionListRecipient[] | null>(null)
  const [resolving, setResolving] = useState(false)

  const cancelledRef = useRef(false)
  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  // Saved lists for the picker + option universes for every select, both
  // fetched once on mount (reused Phase 4 endpoints).
  useEffect(() => {
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      if (!token) return
      setListsLoading(true)
      setOptionsLoading(true)
      const [listsRes, optionsRes] = await Promise.all([
        fetchDistributionListsFiltered(
          [{ key: 'distribution_lists__name', direction: 'asc' }],
          [],
          1,
          null,
          token
        ),
        fetchDistributionListFilterOptions(token)
      ])
      if (cancelledRef.current) return
      setSavedLists(listsRes.data)
      setListsLoading(false)
      setOptions(optionsRes.data ?? null)
      setOptionsLoading(false)
    })()
  }, [])

  /** Cold-load one saved list's full definition (reuses the Phase 4 detail
   * RPC). Returns the row so the screen can pre-populate the editable filters
   * AND seed accounts_add ← account_ids / contacts_add ← contact_ids (§2.13b).
   * Returns null when the list is missing/trashed. */
  const loadListDefinition = useCallback(
    async (listId: string): Promise<DistributionListGridModel | null> => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchDistributionListDetail(listId, token)
      if (res.error) return null
      return res.data ?? null
    },
    []
  )

  /** Generate: resolve the (possibly amended) definition to its recipient
   * set. Reuses view__distribution_list (no new RPC, no writes). */
  const resolve = useCallback(
    async (payload: DistributionListResolvePayload): Promise<string | null> => {
      setResolving(true)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await resolveDistributionList(payload, token)
        if (cancelledRef.current) return null
        if (res.error) {
          setRecipients([])
          return res.error
        }
        setRecipients(res.data)
        return null
      } finally {
        if (!cancelledRef.current) setResolving(false)
      }
    },
    []
  )

  const clearRecipients = useCallback(() => setRecipients(null), [])

  return {
    savedLists,
    listsLoading,
    options,
    optionsLoading,
    recipients,
    resolving,
    loadListDefinition,
    resolve,
    clearRecipients
  }
}
